import React from 'react';
import { ArrowRightOutlined, CheckOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { ObjectCardSection, type ObjectCardTone } from '../components/layout/ObjectCard';

const { Text } = Typography;

export const DetailField: React.FC<{
  label: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}> = ({ label, children, className = '' }) => {
  const value = children == null || children === '' ? '—' : children;

  return (
    <div className={`transport-detail-field ${className}`.trim()}>
      <Text type="secondary" className="transport-detail-field__label">{label}</Text>
      <div className="transport-detail-field__value">{value}</div>
    </div>
  );
};

export const AddressList: React.FC<{ addresses: string[] }> = ({ addresses }) => {
  const visibleAddresses = addresses.filter((address) => address.trim().length > 0);

  if (visibleAddresses.length === 0) {
    return <span>—</span>;
  }

  return (
    <ol className="transport-detail-address-list">
      {visibleAddresses.map((address, index) => (
        <li key={`${address}-${index}`}>{address}</li>
      ))}
    </ol>
  );
};

export interface RouteJourneyStop {
  address: React.ReactNode;
  /** One line per customer order unloaded at this point. */
  details: React.ReactNode[];
}

/** Plain text of a detail line (it may carry <b>), for the `title` of a line cut with „…”. */
const nodeText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return nodeText(node.props.children);
  return '';
};

/** Numbered unloading points, each with what is unloaded there. */
export const StopList: React.FC<{ stops: RouteJourneyStop[] }> = ({ stops }) => {
  if (stops.length === 0) return <span>—</span>;
  return (
    <ol className="transport-detail-address-list transport-detail-address-list--stops">
      {stops.map((stop, index) => (
        <li key={index}>
          <span className="transport-detail-stop-address">{stop.address}</span>
          {stop.details.map((detail, detailIndex) => (
            <span key={detailIndex} className="transport-detail-stop-detail" title={nodeText(detail) || undefined}>{detail}</span>
          ))}
        </li>
      ))}
    </ol>
  );
};

interface RouteJourneyProps {
  loadingLabel: React.ReactNode;
  deliveryLabel: React.ReactNode;
  loadingAddresses: string[];
  deliveryAddresses: string[];
  /** When given, replaces the plain delivery address list. */
  deliveryStops?: RouteJourneyStop[];
  dates: Array<{ label: React.ReactNode; value: React.ReactNode }>;
  extra?: React.ReactNode;
}

export const RouteJourney: React.FC<RouteJourneyProps> = ({
  loadingLabel,
  deliveryLabel,
  loadingAddresses,
  deliveryAddresses,
  deliveryStops,
  dates,
  extra,
}) => (
  <div className="transport-detail-journey" data-testid="transport-detail-journey">
    <div className="transport-detail-journey__stops">
      <div className="transport-detail-stop">
        <span className="transport-detail-stop__icon"><EnvironmentOutlined /></span>
        <DetailField label={loadingLabel}>
          <AddressList addresses={loadingAddresses} />
        </DetailField>
      </div>
      <div className="transport-detail-journey__line" aria-hidden="true">
        <ArrowRightOutlined />
      </div>
      <div className="transport-detail-stop transport-detail-stop--delivery">
        <span className="transport-detail-stop__icon"><EnvironmentOutlined /></span>
        <DetailField label={deliveryLabel}>
          {deliveryStops ? <StopList stops={deliveryStops} /> : <AddressList addresses={deliveryAddresses} />}
        </DetailField>
      </div>
    </div>
    {dates.length > 0 || extra ? (
      <div className="transport-detail-date-grid">
        {dates.map((date, index) => (
          <DetailField key={index} label={date.label}>{date.value}</DetailField>
        ))}
        {extra}
      </div>
    ) : null}
  </div>
);

/**
 * One section of the truck card on the object-card primitives
 * (components/layout/ObjectCard.tsx, direction 2026-10-19): icon tile, 15 px
 * title, count, one-line hint, small outline actions; `wide` spans the row.
 */
export const DetailSection: React.FC<{
  title: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  tone?: ObjectCardTone;
  count?: number;
  hint?: React.ReactNode;
  wide?: boolean;
  testId?: string;
}> = ({ title, children, extra, className = '', icon, tone, count, hint, wide, testId }) => (
  <ObjectCardSection
    title={title}
    icon={icon}
    tone={tone}
    count={count}
    hint={hint}
    actions={extra}
    wide={wide}
    testId={testId}
    className={`transport-detail-section ${className}`.trim()}
  >
    {children}
  </ObjectCardSection>
);

export interface TransportRailStep {
  key: string;
  label: React.ReactNode;
}

/**
 * Stage rail of the truck (the ZK rail markup in an object-card section, not clickable): passed stages
 * filled with a tick, the current one outlined with its number, later ones
 * grey. `currentIndex` null = no current stage (cancelled truck); `closed`
 * marks the last stage as passed (delivered); `interrupted` paints the
 * current stage as a warning (delayed).
 */
export const TransportStatusRail: React.FC<{
  steps: TransportRailStep[];
  currentIndex: number | null;
  closed?: boolean;
  interrupted?: boolean;
  ariaLabel: string;
}> = ({ steps, currentIndex, closed = false, interrupted = false, ariaLabel }) => (
  <section className="obj-section transport-detail-rail" aria-label={ariaLabel} data-testid="transport-detail-rail">
    <div className="zk-rail-wrap">
      <ol className="zk-rail zk-rail--steps-3">
        {steps.map((step, index) => {
          const done = currentIndex !== null && (index < currentIndex || (closed && index === currentIndex));
          const current = currentIndex !== null && index === currentIndex && !done;
          const classes = [
            'zk-rail__item',
            done ? 'is-done' : '',
            current ? 'is-current' : '',
            current && interrupted ? 'is-interrupted' : '',
          ].filter(Boolean).join(' ');
          return (
            <li key={step.key} className={classes} aria-current={current ? 'step' : undefined}>
              <span className="zk-rail__button">
                <span className="zk-rail__dot" aria-hidden="true">
                  {done ? <CheckOutlined /> : index + 1}
                </span>
                <span className="zk-rail__label">{step.label}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  </section>
);
